import axios from 'axios';
import { ANALYTICS_API_BASE_URL } from '../Config/Config';
import { UpdateAnalyticsProfile, GetAnalyticsProfile } from './HeedifyServices';

/**
 * Execute a predefined analytics query
 * @param {string} tenantId - Tenant identifier
 * @param {string} queryId - Query ID from predefined-queries.json (e.g., "total-calls")
 * @param {object} filters - Filter values (dateRange, queueId, direction, etc.)
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Query results
 */
export async function executeAnalyticsQuery(tenantId, queryId, filters = {}, token) {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/query/${tenantId}`;

        const requestBody = {
            queryId,
            filters: {
                ...filters,
                tenantId // Ensure tenantId is in filters
            }
        };

        const response = await axios.post(url, requestBody, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            timeout: 30000 // 30 secondes pour Cosmos DB (beaucoup plus rapide que Synapse)
        });

        if (response.data.success) {
            return {
                success: true,
                data: response.data.data || response.data.result, // Support both formats
                metadata: response.data.metadata
            };
        } else {
            return {
                success: false,
                error: response.data.error
            };
        }
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.error || error.message || 'Unknown error'
        };
    }
}

/**
 * Récupère un SAS de lecture pour l'enregistrement (ou la transcription) d'un appel.
 * Le SAS est généré côté Data_Pipe contre le compte de stockage autoritaire
 * (RECORDING_FILE_URL du doc CDR), donc pas de devinette de scope/compte.
 * @param {string} tenantId
 * @param {string} callId
 * @param {string} token
 * @param {'recording'|'transcript'} type
 * @returns {Promise<string|null>} sasUrl ou null si indisponible
 */
export async function getRecordingSasUrl(tenantId, callId, token, type = 'recording') {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/recording-sas/${tenantId}/${callId}?type=${type}`;
        const response = await axios.get(url, {
            headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
            timeout: 30000
        });
        return response.data?.success ? (response.data.sasUrl || null) : null;
    } catch (error) {
        // 404 = pas d'enregistrement pour cet appel → null (pas une erreur bloquante)
        return null;
    }
}

/**
 * Récupère la transcription d'un appel (téléchargée + parsée côté Data_Pipe → pas de CORS).
 * @returns {Promise<{segments: Array, text: string}|null>} segments structurés + texte, ou null
 */
export async function getRecordingTranscript(tenantId, callId, token) {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/recording-transcript/${tenantId}/${callId}`;
        const response = await axios.get(url, {
            headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
            timeout: 30000
        });
        if (!response.data?.success) return null;
        return { segments: response.data.segments || [], text: response.data.text || '' };
    } catch (error) {
        return null;
    }
}

/**
 * Liste paginée des appels enregistrés (page /recording), depuis Data_Pipe.
 * Source = doc CDR Cosmos filtré sur RECORDING_ENABLED = true.
 * @param {string} tenantId
 * @param {object} filters - { scope?, startDate?, endDate?, direction?, agentId?, callerId?, callId?, durationMin?, durationMax? }
 * @param {string} token
 * @param {number} pageSize
 * @param {string|null} continuationToken - token de la page précédente (null = 1ʳᵉ page)
 * @returns {Promise<{success, data, continuationToken, hasMore}>}
 */
export async function getRecordedCallsList(tenantId, filters = {}, token, pageSize = 25, continuationToken = null) {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/recorded-calls-list/${tenantId}`;
        const response = await axios.post(url, { filters, pageSize, continuationToken }, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            timeout: 30000
        });
        if (response.data?.success) {
            return {
                success: true,
                data: response.data.data || [],
                continuationToken: response.data.continuationToken || null,
                hasMore: !!response.data.hasMore
            };
        }
        return { success: false, data: [], continuationToken: null, hasMore: false, error: response.data?.error };
    } catch (error) {
        return { success: false, data: [], continuationToken: null, hasMore: false, error: error.response?.data?.error || error.message };
    }
}

/**
 * Fetch data for a widget configuration
 * @param {object} widgetConfig - Widget configuration including queryId
 * @param {object} filterValues - Current filter values from dashboard
 * @param {string} tenantId - Tenant identifier
 * @param {string} token - Authorization token
 * @returns {Promise<any>} Widget data in appropriate format
 */
export async function fetchWidgetData(widgetConfig, filterValues, tenantId, token) {
    try {
        console.log("🔍 Fetching data for widget (Query ID: ${widgetConfig || widgetConfig.query}):", widgetConfig);
        // Extract queryId from widget config
        const queryId = widgetConfig.queryId || widgetConfig.query;

        if (!queryId) {
            throw new Error('Widget configuration missing queryId');
        }

        // Mêmes règles que buildWidgetFilters : les seuils de calcul viennent du profil,
        // pas de la config d'affichage du widget.
        const filters = buildWidgetFilters(widgetConfig, filterValues);

        // Execute the query
        const result = await executeAnalyticsQuery(tenantId, queryId, filters, token);

        if (!result.success) {
            throw new Error(result.error);
        }

        // Transform data based on widget type if needed
        return transformDataForWidget(result.data, widgetConfig.type);
    } catch (error) {
        throw error;
    }
}

/**
 * Build the filters payload for one widget — same logic as fetchWidgetData but isolé
 * pour pouvoir être appelé en batch (sans déclencher de fetch).
 */
export function buildWidgetFilters(widgetConfig, filterValues) {
    // Les seuils de CALCUL viennent du profil analytics (reportConfig.thresholds) et sont
    // injectés dans `filterValues` par le dashboard — ils s'appliquent donc à toutes les
    // requêtes de la même façon.
    //
    // On ne transmet PLUS les valeurs de `widget.config` : ce sont des paramètres
    // d'AFFICHAGE (cibles de colorisation), et les faire passer pour des filtres de requête
    // produisait des collisions silencieuses — la cible « SLA >= 85 % » du tableau partait
    // sous le nom `slaThreshold`, que le backend lisait comme « SLA = 85 secondes ».
    return {
        ...filterValues,
        ...widgetConfig.filters
    };
}

/**
 * Batch endpoint : envoie N queries au serveur en 1 round-trip.
 * Le serveur exécute les queries en Promise.all (vraie parallélisation Cosmos).
 *
 * @param {string} tenantId
 * @param {Array<{widgetId, queryId, widgetType, filters}>} queries  Préparé par buildWidgetFilters
 * @param {string} token
 * @returns {Promise<{success, results, error?}>} results: [{widgetId, data?, error?, cached?}]
 */
export async function fetchBatchWidgetData(tenantId, queries, token) {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/query-batch/${tenantId}`;

        // On envoie au serveur juste ce dont il a besoin : widgetId, queryId, filters
        const payload = {
            queries: queries.map(q => ({
                widgetId: q.widgetId,
                queryId: q.queryId,
                filters: { ...q.filters, tenantId }
            }))
        };

        const response = await axios.post(url, payload, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            timeout: 60000  // 60s — endpoint agrégé peut prendre plus longtemps qu'une seule query
        });

        if (!response.data?.success || !Array.isArray(response.data.results)) {
            return { success: false, error: response.data?.error || 'Invalid batch response' };
        }

        // Mapper widgetType → transformDataForWidget pour rester compatible avec les widgets
        const widgetTypeById = {};
        queries.forEach(q => { widgetTypeById[q.widgetId] = q.widgetType; });

        const results = response.data.results.map(r => {
            if (r.error) {
                return { widgetId: r.widgetId, error: r.error };
            }
            return {
                widgetId: r.widgetId,
                data: transformDataForWidget(r.data, widgetTypeById[r.widgetId]),
                cached: r.cached
            };
        });

        return { success: true, results, metadata: response.data.metadata };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.error || error.message || 'Unknown error'
        };
    }
}

/**
 * Transform query results to match widget expectations
 * @param {any} data - Raw query results (typically array of rows from SQL)
 * @param {string} widgetType - Widget type (metric, bar, line, etc.)
 * @returns {any} Transformed data
 */
function transformDataForWidget(data, widgetType) {
    // Handle null/undefined data
    if (!data) {
        return null;
    }

    // SQL queries return arrays of rows - transform based on widget type
    switch (widgetType) {
        case 'metric':
            // Metric widgets expect {value, trend} object
            // SQL query returns [{value: 123, trend: 5.2}]
            if (Array.isArray(data) && data.length > 0) {
                return data[0]; // Return first row as object
            }
            return { value: 0, trend: 0 };

        case 'bar':
        case 'line':
        case 'area':
        case 'pie':
        case 'donut':
            // Chart widgets expect array of {name, value} objects
            // SQL already returns in this format
            return Array.isArray(data) ? data : [];

        case 'table':
            // Table widgets expect array of objects
            return Array.isArray(data) ? data : [];

        case 'gauge':
            // Gauge expects {value, min, max}
            if (Array.isArray(data) && data.length > 0) {
                return data[0];
            }
            return { value: 0, min: 0, max: 100 };

        default:
            // Return data as-is for unknown types
            return data;
    }
}

/**
 * Get available queries from the backend
 * @returns {Promise<object>} Available queries catalog
 */
export async function getAvailableQueries() {
    try {
        // This could be an API call to get the queries catalog
        // For now, we'll use a static import or hardcoded list
        return {
            success: true,
            queries: [] // TODO: fetch from backend or import from schema
        };
    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
}

// ------------------ ANALYTICS LAYOUT ------------------
// Old Portal Settings methods removed - now using Analytics Profile service only

/**
 * Save Dashboard Layout (tabs, widgets, positions) to Analytics Profile
 * @param {string} tenantId - Tenant ID
 * @param {string} analyticsProfileId - Analytics Profile ID
 * @param {Object} dashboardLayout - Complete dashboard layout object
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response with success status
 */
export async function saveDashboardLayout(tenantId, analyticsProfileId, dashboardLayout, token) {
    try {
        console.log('🔗 saveDashboardLayout called with:', { tenantId, analyticsProfileId, hasToken: !!token });

        if (!analyticsProfileId) {
            console.error('❌ analyticsProfileId is missing!');
            return {
                success: false,
                message: 'Analytics Profile ID is required'
            };
        }

        // Récupérer le profil analytics actuel pour ne modifier que le dashboardLayout
        const currentProfile = await GetAnalyticsProfile(tenantId, analyticsProfileId, token);

        if (!currentProfile || !currentProfile.success) {
            console.error('❌ Failed to get current analytics profile', currentProfile?.status);
            return {
                success: false,
                // Le statut de la RELECTURE compte autant que celui de l'écriture : c'est elle
                // qui échoue en premier quand le token a expiré (401). Sans lui, l'UI ne pouvait
                // afficher qu'un « réessayez » générique.
                status: currentProfile?.status,
                message: 'Failed to retrieve analytics profile'
            };
        }

        // Construire l'objet à mettre à jour avec le dashboardLayout
        const updatedProfile = {
            ...currentProfile.data,
            dashboardLayout: {
                tabs: dashboardLayout.tabs || {}
            }
        };

        console.log('📦 Updating analytics profile with dashboardLayout');

        // Utiliser UpdateAnalyticsProfile de HeedifyServices
        const response = await UpdateAnalyticsProfile(tenantId, analyticsProfileId, updatedProfile, token);

        if (response && response.success) {
            return {
                success: true,
                message: 'Dashboard layout saved successfully',
                data: response.data
            };
        } else {
            return {
                success: false,
                status: response?.status,   // ex. 403 → droits insuffisants (remonté à l'UI)
                message: response?.message || 'Failed to save dashboard layout'
            };
        }
    } catch (error) {
        console.error('Error saving dashboard layout:', error);
        return {
            success: false,
            status: error.response?.status,
            message: error.response?.data?.message || error.message
        };
    }
}

/**
 * Get Dashboard Layout from Analytics Profile
 * @param {string} tenantId - Tenant ID
 * @param {string} analyticsProfileId - Analytics Profile ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Dashboard layout object
 */
export async function getDashboardLayout(tenantId, analyticsProfileId, token) {
    try {
        console.log('📥 getDashboardLayout called with:', { tenantId, analyticsProfileId });

        // Utiliser GetAnalyticsProfile de HeedifyServices
        const response = await GetAnalyticsProfile(tenantId, analyticsProfileId, token);

        if (response && response.success && response.data) {
            const dashboardLayout = response.data.dashboardLayout;

            // Si le dashboardLayout existe, le retourner
            if (dashboardLayout && dashboardLayout.tabs) {
                return {
                    success: true,
                    data: dashboardLayout
                };
            }

            // Sinon, retourner une structure par défaut
            console.log('ℹ️ No dashboardLayout found, returning default structure');
            return {
                success: true,
                data: {
                    tabs: {
                        dashboard: {
                            displayName: "Dashboard",
                            type: "system",
                            widgets: {},
                            widgetsPositions: {},
                            createdAt: new Date().toISOString()
                        },
                        calls: {
                            displayName: "Calls List",
                            type: "system",
                            widgets: {},
                            widgetsPositions: {},
                            createdAt: new Date().toISOString()
                        }
                    }
                }
            };
        } else {
            console.warn('⚠️ Failed to load analytics profile');
            return {
                success: false,
                message: 'Failed to load analytics profile',
                data: {
                    tabs: {
                        dashboard: {
                            displayName: "Dashboard",
                            type: "system",
                            widgets: {},
                            widgetsPositions: {},
                            createdAt: new Date().toISOString()
                        },
                        calls: {
                            displayName: "Calls List",
                            type: "system",
                            widgets: {},
                            widgetsPositions: {},
                            createdAt: new Date().toISOString()
                        }
                    }
                }
            };
        }
    } catch (error) {
        console.error('Error getting dashboard layout:', error);
        return {
            success: false,
            message: error.message,
            data: {
                tabs: {
                    dashboard: {
                        displayName: "Dashboard",
                        type: "system",
                        widgets: {},
                        widgetsPositions: {},
                        createdAt: new Date().toISOString()
                    },
                    calls: {
                        displayName: "Calls List",
                        type: "system",
                        widgets: {},
                        widgetsPositions: {},
                        createdAt: new Date().toISOString()
                    }
                }
            }
        };
    }
}

export default {
    executeAnalyticsQuery,
    fetchWidgetData,
    getAvailableQueries,
    saveDashboardLayout,
    getDashboardLayout
};
